import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Select, Spin } from "antd";
import { debounce } from "lodash";

/**
 * Controlled, searchable remote select. `value` is { value, label, ... } (single) or an
 * array of those (multiple), so chips keep their labels after the options list changes.
 *
 * fetchOptions(page, search) -> [{ value, label, ...extra }]
 * pasteLookup(emailsCsv)     -> { found: [{ value, label, ... }], notFound: [email] }  (optional)
 */
const RemoteSelect = ({
  value,
  onChange,
  fetchOptions,
  mode,
  placeholder,
  renderOption,
  pasteLookup,
  disabledValues = [],
  disabledHint,
  ariaLabel,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [notFound, setNotFound] = useState([]);
  const [opened, setOpened] = useState(false); // options load the first time the list is opened
  const reqId = useRef(0);

  const load = useCallback(
    async (term, pageNo) => {
      const id = ++reqId.current;
      setLoading(true);
      try {
        const res = (await fetchOptions(pageNo, term)) || [];
        if (id !== reqId.current) return;
        setOptions((prev) => {
          if (pageNo === 0) return res;
          const seen = new Set(prev.map((o) => o.value));
          return [...prev, ...res.filter((o) => !seen.has(o.value))];
        });
        setHasMore(res.length > 0);
      } catch {
        if (id === reqId.current && pageNo === 0) setOptions([]);
        setHasMore(false);
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    },
    [fetchOptions]
  );

  useEffect(() => { if (opened) load(search, page); }, [opened, search, page, load]);

  const debounced = useMemo(() => debounce((term) => { setPage(0); setHasMore(true); setSearch(term); }, 400), []);
  useEffect(() => () => debounced.cancel(), [debounced]);

  const selected = useMemo(() => (mode === "multiple" ? value || [] : value ? [value] : []), [mode, value]);
  const byValue = useMemo(() => {
    const m = new Map(options.map((o) => [o.value, o]));
    selected.forEach((o) => m.set(o.value, { ...m.get(o.value), ...o }));
    return m;
  }, [options, selected]);

  const handleChange = (val) => {
    setNotFound([]);
    if (mode === "multiple") onChange((val || []).map((v) => byValue.get(v.value) || v));
    else onChange(val ? byValue.get(val.value) || val : null);
    if (search) { setSearch(""); setPage(0); setHasMore(true); }
  };

  const handlePaste = async (e) => {
    if (!pasteLookup || mode !== "multiple") return;
    const text = e.clipboardData.getData("text");
    if (!text || !text.includes("@")) return;
    e.preventDefault();
    const emails = text.split(/[,\n\s;]+/).map((x) => x.trim()).filter(Boolean).join(",");
    setLoading(true);
    try {
      const { found = [], notFound: missing = [] } = (await pasteLookup(emails)) || {};
      const current = new Map(selected.map((o) => [o.value, o]));
      found.forEach((o) => current.set(o.value, o));
      onChange([...current.values()]);
      setNotFound(missing);
    } catch {
      setNotFound(emails.split(","));
    } finally {
      setLoading(false);
    }
  };

  const disabledSet = new Set(disabledValues);
  return (
    <div onPaste={handlePaste}>
      <Select
        aria-label={ariaLabel}
        mode={mode}
        labelInValue
        showSearch
        allowClear
        filterOption={false}
        placeholder={placeholder}
        value={mode === "multiple" ? selected.map((o) => ({ value: o.value, label: o.label })) : value ? { value: value.value, label: value.label } : undefined}
        onChange={handleChange}
        onSearch={(t) => debounced(t)}
        onDropdownVisibleChange={(open) => open && setOpened(true)}
        onPopupScroll={(e) => {
          const t = e.target;
          if (t.scrollTop + t.offsetHeight >= t.scrollHeight - 2 && !loading && hasMore) setPage((p) => p + 1);
        }}
        notFoundContent={loading ? <Spin size="small" /> : "No matches"}
        loading={loading}
        style={{ width: "100%" }}
        listHeight={260}
        optionLabelProp="label"
        tagRender={mode === "multiple" ? (p) => (
          <span className="chip" title={typeof p.label === "string" ? p.label : undefined} onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}>
            <span>{p.label}</span>
            {p.closable && <button type="button" aria-label={`Remove ${p.label}`} onClick={p.onClose}>✕</button>}
          </span>
        ) : undefined}
      >
        {options.map((o) => (
          <Select.Option key={o.value} value={o.value} label={o.label} disabled={disabledSet.has(o.value) && !selected.some((s) => s.value === o.value)}>
            {renderOption ? renderOption(o, disabledSet.has(o.value) ? disabledHint : null) : o.label}
          </Select.Option>
        ))}
      </Select>
      {notFound.length > 0 && (
        <div className="rerr" style={{ fontSize: 12, marginTop: 4 }}>No user found for: {notFound.join(", ")}</div>
      )}
    </div>
  );
};

export default RemoteSelect;
